import React, { useState, useRef, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Camera, Flag, Focus, Gauge, Hand, Images, Send, Volume2, VolumeX, X } from 'lucide-react';
import { photoApi } from '../api/photoApi';
import RoundConditionsPanel from '../components/RoundConditionsPanel';
import { useDeviceCourses } from '../context/DeviceCoursesContext';
import { useProfile } from '../context/ProfileContext';
import { useRoundConditions } from '../hooks/useRoundConditions';
import { Photo, ShotAnalysis, UploadCredits } from '../types';
import { distanceUnitLabel, toCanonicalDistanceYards, toDisplayDistance } from '../constants/units';

const isMobileDevice = () =>
  typeof navigator !== 'undefined' &&
  /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

interface CaddieResultPanelProps {
  analysis: ShotAnalysis;
  photo: Photo;
  measurementSystem: 'imperial' | 'metric';
  onReadAgain: () => void;
  onStop: () => void;
}

const CaddieResultPanel: React.FC<CaddieResultPanelProps> = ({ analysis, photo, measurementSystem, onReadAgain, onStop }) => {
  const confidence = analysis.stanceRecommendation?.confidence;
  const confidencePercent = confidence == null
    ? null
    : Math.round((confidence <= 1 ? confidence : confidence / 100) * 100);
  const context = [
    photo.distanceToHoleYards == null
      ? null
      : `${Math.round(toDisplayDistance(photo.distanceToHoleYards, measurementSystem))} ${distanceUnitLabel(measurementSystem)}`,
    analysis.ballLieAnalysis?.lieType || analysis.ballLieDescription,
  ].filter(Boolean).join(' · ');
  const spokenAdvice = analysis.overallRecommendation
    || analysis.stanceRecommendation?.spokenSummary
    || analysis.ballLieDescription;
  const metrics: Array<{ label: string; value?: string; icon: React.ReactElement }> = [
    { label: 'Stance', value: analysis.stanceAdvice, icon: <i className="pi pi-arrows-h" aria-hidden="true" /> },
    { label: 'Grip', value: analysis.gripAdvice, icon: <Hand aria-hidden="true" /> },
    { label: 'Club face', value: analysis.clubFaceOrientation, icon: <Focus aria-hidden="true" /> },
    { label: 'Tempo', value: analysis.swingSpeedRecommendation, icon: <Gauge aria-hidden="true" /> },
  ];
  const populatedMetrics = metrics.filter((metric) => Boolean(metric.value));
  const requiresPhotoRetake = analysis.requiresPhotoRetake === true;

  return (
    <section className="caddie-result" aria-live="polite">
      <div className="caddie-result__call">
        <div className="caddie-result__title"><Flag aria-hidden="true" /> {requiresPhotoRetake ? 'Photo needs another look' : "Your caddie's call"}</div>
        {!requiresPhotoRetake && context && <div className="caddie-result__context">{context}</div>}
        <div className="caddie-result__club-row">
          <div>
            <strong className="caddie-result__club">{requiresPhotoRetake ? 'Try another photo' : analysis.recommendedClub || 'Play the shot'}</strong>
            {!requiresPhotoRetake && analysis.stanceRecommendation?.reasoningSummary && (
              <span className="caddie-result__approach">{analysis.stanceRecommendation.reasoningSummary}</span>
            )}
          </div>
          {!requiresPhotoRetake && confidencePercent != null && (
            <span className="caddie-result__confidence">{confidencePercent}% confidence</span>
          )}
        </div>
      </div>

      {spokenAdvice && (
        <div className="caddie-result__spoken">
          <button type="button" onClick={onReadAgain} aria-label="Read recommendation aloud">
            <Volume2 aria-hidden="true" />
          </button>
          <div><strong>Hear your recommendation</strong><p>{spokenAdvice}</p></div>
        </div>
      )}

      {populatedMetrics.length > 0 && (
        <div className="caddie-result__setup">
          <h4>Set up the shot</h4>
          <div className="caddie-result__grid">
            {populatedMetrics.map((metric) => (
              <div className="caddie-result__metric" key={metric.label}>
                <span className="caddie-result__metric-icon">{metric.icon}</span>
                <strong>{metric.label}</strong>
                <p>{metric.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {!requiresPhotoRetake && analysis.ballLieDescription && (
        <div className="caddie-result__lie"><strong>Lie read:</strong> {analysis.ballLieDescription}</div>
      )}

      <div className="caddie-audio-controls">
        <Button label="Read Again" icon={<Volume2 className="ui-icon" strokeWidth={1.5} />} outlined size="small" className="read-again-btn" onClick={onReadAgain} />
        <Button label="Stop" icon={<VolumeX className="ui-icon" strokeWidth={1.5} />} outlined size="small" severity="secondary" className="stop-speaking-btn" onClick={onStop} />
      </div>
    </section>
  );
};

const UploadPage: React.FC = () => {
  const { profile } = useProfile();
  const { currentHole, selectedCourse, setCurrentHole } = useDeviceCourses();
  const roundConditions = useRoundConditions(selectedCourse, currentHole);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [hasPreview, setHasPreview] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isWebcamOpen, setIsWebcamOpen] = useState(false);
  const [distance, setDistance] = useState('');
  const [latestAnalysis, setLatestAnalysis] = useState<Photo | null>(null);
  const [credits, setCredits] = useState<UploadCredits | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const previewObjectUrl = useRef<string | null>(null);
  const speechObjectUrl = useRef<string | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const toast = useRef<Toast>(null);

  const handleFileSelected = (file: File) => {
    setSelectedFile(file);
    setLatestAnalysis(null);
    if (previewObjectUrl.current) {
      URL.revokeObjectURL(previewObjectUrl.current);
    }
    previewObjectUrl.current = URL.createObjectURL(file);
    setHasPreview(true);
  };

  // Load the golfer's remaining Caddie Credits so we can show the balance and block at zero.
  const refreshCredits = React.useCallback(async () => {
    if (!profile.userId) return;
    try {
      setCredits(await photoApi.getCredits(profile.userId));
    } catch {
      // Non-fatal: if the balance can't be read, fall back to letting the API's 402 guard decide.
      setCredits(null);
    }
  }, [profile.userId]);

  useEffect(() => {
    refreshCredits();
  }, [refreshCredits]);

  useEffect(() => {
    if (hasPreview && imgRef.current && previewObjectUrl.current) {
      imgRef.current.src = previewObjectUrl.current;
    }
  }, [hasPreview]);

  // Attach the webcam stream to the video element once the capture view is open
  useEffect(() => {
    if (isWebcamOpen && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [isWebcamOpen]);

  // Stop the camera and release the preview URL when the component unmounts
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (previewObjectUrl.current) {
        URL.revokeObjectURL(previewObjectUrl.current);
      }
      if (speechObjectUrl.current) {
        URL.revokeObjectURL(speechObjectUrl.current);
      }
    };
  }, []);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelected(file);
  };

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  // Take a picture: use the native camera on phones, and the webcam on desktop.
  const handleTakePicture = async () => {
    if (isMobileDevice() || !navigator.mediaDevices?.getUserMedia) {
      cameraInputRef.current?.click();
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });
      streamRef.current = stream;
      setIsWebcamOpen(true);
    } catch {
      toast.current?.show({
        severity: 'error',
        summary: 'Camera unavailable',
        detail: 'Could not access the webcam. Choose a photo from your gallery instead.',
        life: 4000,
      });
    }
  };

  const handleCaptureFromWebcam = () => {
    const video = videoRef.current;
    if (!video) return;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `shot-${Date.now()}.png`, { type: 'image/png' });
      handleFileSelected(file);
      closeWebcam();
    }, 'image/png');
  };

  const closeWebcam = () => {
    stopStream();
    setIsWebcamOpen(false);
  };

  const handleRemovePhoto = () => {
    setSelectedFile(null);
    setHasPreview(false);
    setLatestAnalysis(null);
    if (previewObjectUrl.current) {
      URL.revokeObjectURL(previewObjectUrl.current);
      previewObjectUrl.current = null;
    }
    if (imgRef.current) imgRef.current.src = '';
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const clearSelectedPhotoAfterSubmit = () => {
    setSelectedFile(null);
    setHasPreview(false);
    if (previewObjectUrl.current) {
      URL.revokeObjectURL(previewObjectUrl.current);
      previewObjectUrl.current = null;
    }
    if (imgRef.current) imgRef.current.src = '';
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const stopSpeaking = () => {
    const el = audioElementRef.current;
    if (el) {
      el.pause();
      el.currentTime = 0;
    }
  };

  const speakAnalysis = async (photo: Photo) => {
    try {
      const audio = await photoApi.getSpeech(photo.id);
      if (speechObjectUrl.current) URL.revokeObjectURL(speechObjectUrl.current);
      speechObjectUrl.current = URL.createObjectURL(audio);
      stopSpeaking();
      const audioEl = new Audio(speechObjectUrl.current);
      audioElementRef.current = audioEl;
      await audioEl.play();
    } catch (err) {
      // Surface Azure Speech failures instead of masking them with a device-voice
      // fallback — a silent fallback hides that server-side synthesis is misconfigured.
      console.error('Azure Speech readout failed', err);
      const status =
        typeof err === 'object' && err !== null && 'response' in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      toast.current?.show({
        severity: 'error',
        summary: 'Voice readout unavailable',
        detail:
          status === 503
            ? 'Azure Speech is not configured correctly (503). Check the API Speech settings.'
            : 'Could not play the Azure Speech readout.',
        life: 6000,
      });
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.current?.show({ severity: 'warn', summary: 'No photo selected', detail: 'Please take or choose a photo first.', life: 3000 });
      return;
    }

    if (outOfCredits) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Out of Caddie Credits',
        detail: 'Redeem a plan link from your CaddieSight contact to keep analyzing shots.',
        life: 5000,
      });
      return;
    }

    setIsUploading(true);
    try {
      const parsed = Number(distance);
      const distanceToHoleYards = roundConditions.greenDistanceYards
        ?? (distance.trim() && Number.isFinite(parsed)
          ? toCanonicalDistanceYards(parsed, profile.measurementSystem)
          : undefined);

      const photo = await photoApi.upload({
        file: selectedFile,
        description: '',
        user: profile.userId,
        latitude: roundConditions.location?.latitude,
        longitude: roundConditions.location?.longitude,
        courseName: selectedCourse?.name,
        courseId: roundConditions.courseDetail?.id,
        holeNumber: selectedCourse ? currentHole : undefined,
        distanceToHoleYards,
      });

      // Ask the caddie for a recommendation right away (best-effort).
      let analyzedPhoto: Photo | null = photo.analysis ? photo : null;
      try {
        if (!analyzedPhoto) {
          analyzedPhoto = await photoApi.analyze(photo.id);
        }
        if (analyzedPhoto) {
          setLatestAnalysis(analyzedPhoto);
          await speakAnalysis(analyzedPhoto);
        }
      } catch {
        // the photo still uploaded even if analysis fails
      }

      toast.current?.show({
        severity: 'success',
        summary: analyzedPhoto ? 'Caddie recommendation ready' : 'Shot uploaded',
        detail: analyzedPhoto ? 'Your Digital Caddie has weighed in.' : 'Your shot uploaded, but analysis was not available yet.',
        life: 4000,
      });
      setDistance('');
      clearSelectedPhotoAfterSubmit();
      // Refresh the balance: a successful recommendation consumes a credit, while a photo-retake
      // rejection does not, so re-fetch to show the golfer their true remaining count either way.
      refreshCredits();
    } catch (err) {
      const status =
        typeof err === 'object' && err !== null && 'response' in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      if (status === 402) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Out of Caddie Credits',
          detail: "You've used all your Caddie Credits. Redeem a plan link to get more.",
          life: 5000,
        });
        refreshCredits();
      } else {
        toast.current?.show({ severity: 'error', summary: 'Upload failed', detail: 'Could not upload your photo. Please try again.', life: 4000 });
      }
    } finally {
      setIsUploading(false);
    }
  };

  const outOfCredits = credits != null && !credits.unlimited && credits.remaining <= 0;

  return (
    <div className="upload-page">
      <Toast ref={toast} />

      {selectedCourse && (
        <RoundConditionsPanel
          course={selectedCourse}
          courseDetailStatus={roundConditions.courseDetailStatus}
          currentHole={currentHole}
          distanceYards={roundConditions.greenDistanceYards}
          holeCount={roundConditions.courseDetail?.holeCount ?? selectedCourse.holes}
          isRefreshing={roundConditions.isRefreshing}
          locationStatus={roundConditions.locationStatus}
          manualDistance={distance}
          measurementSystem={profile.measurementSystem}
          onHoleChange={setCurrentHole}
          onManualDistanceChange={setDistance}
          onRefresh={roundConditions.refresh}
          wind={roundConditions.wind}
        />
      )}

      {credits && !credits.unlimited && (
        <div className={`credits-banner${outOfCredits ? ' credits-banner--empty' : ''}`} role="status">
          <i className={`pi ${outOfCredits ? 'pi-exclamation-triangle' : 'pi-ticket'}`} aria-hidden="true" />
          {outOfCredits ? (
            <span>You're out of Caddie Credits. Redeem a plan link to keep analyzing shots.</span>
          ) : (
            <span>{credits.remaining.toLocaleString()} Caddie Credits remaining</span>
          )}
        </div>
      )}


      {isWebcamOpen && (
        <div className="webcam-overlay" role="dialog" aria-modal="true" aria-label="Webcam capture">
          <div className="webcam-modal">
            <video ref={videoRef} className="webcam-video" playsInline muted />
            <div className="webcam-actions">
              <Button label="Capture" icon={<Camera className="ui-icon" strokeWidth={1.5} />} className="btn-camera" onClick={handleCaptureFromWebcam} />
              <Button label="Cancel" icon={<X className="ui-icon" strokeWidth={1.5} />} severity="secondary" outlined onClick={closeWebcam} />
            </div>
          </div>
        </div>
      )}

      <div className="card-surface">
        {selectedFile ? (
          <div className="photo-preview">
            {hasPreview && <img ref={imgRef} alt="Selected golf shot" />}
            <Button
              icon={<X className="ui-icon" strokeWidth={1.5} />}
              rounded
              severity="danger"
              className="remove-btn"
              size="small"
              aria-label="Remove photo"
              onClick={handleRemovePhoto}
            />
            <p className="preview-filename">{selectedFile.name}</p>
          </div>
        ) : (
          <div className="upload-placeholder">
            <Camera className="upload-icon" strokeWidth={1.25} aria-hidden="true" />
            <h3>Caddie your lie</h3>
          </div>
        )}

        {/* Hidden file inputs */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
          aria-label="Select photo from gallery"
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
          aria-label="Take a photo"
        />

        {/* Action buttons */}
        <div className="upload-actions">
          <Button
            label="Take a Picture"
            icon={<Camera className="ui-icon" strokeWidth={1.5} />}
            className="btn-camera"
            onClick={handleTakePicture}
            disabled={isUploading}
          />
          <Button
            label="Choose from Gallery"
            icon={<Images className="ui-icon" strokeWidth={1.5} />}
            className="btn-gallery"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          />
        </div>

        {/* Submit */}
        <div className="upload-submit">
          {isUploading ? (
            <div>
              <ProgressSpinner style={{ width: 50, height: 50 }} />
              <p className="uploading-text">Uploading and asking your caddie...</p>
            </div>
          ) : (
            <Button
              label="Submit Shot to Caddie"
              icon={<Send className="ui-icon" strokeWidth={1.5} />}
              className="btn-submit"
              onClick={handleUpload}
              disabled={!selectedFile || outOfCredits}
            />
          )}
        </div>

        {latestAnalysis?.analysis && (
          <CaddieResultPanel
            analysis={latestAnalysis.analysis}
            photo={latestAnalysis}
            measurementSystem={profile.measurementSystem}
            onReadAgain={() => { void speakAnalysis(latestAnalysis); }}
            onStop={stopSpeaking}
          />
        )}
      </div>
    </div>
  );
};

export default UploadPage;
